import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { AuthFrame } from '@/components/AuthFrame';
import { Field } from '@/components/ui/Field';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { authClient } from '@/lib/auth-client';
import { Link } from '@/lib/router-compat';
import { meQuery, safeRedirect } from '@/lib/session';

export const Route = createFileRoute('/login')({
  validateSearch: z.object({ redirect: z.string().optional(), expired: z.boolean().optional() }),
  component: LoginPage
});

type Step = 'password' | 'totp' | 'backup';

function LoginPage() {
  const { t } = useTranslation();
  const { redirect, expired } = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [trustDevice, setTrustDevice] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: meQuery().queryKey });
    await navigate({ to: safeRedirect(redirect) as never, replace: true });
  };

  const authError = (status: number | undefined) =>
    status === 429 ? t('auth.tooManyAttempts') : step === 'password' ? t('auth.invalidCredentials') : t('auth.invalidCode');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (step === 'password') {
        const { data, error: err } = await authClient.signIn.email({ email, password });
        if (err) return setError(authError(err.status));
        if (data && 'twoFactorRedirect' in data && data.twoFactorRedirect) {
          setStep('totp');
          return;
        }
        return await done();
      }
      const { error: err } =
        step === 'totp'
          ? await authClient.twoFactor.verifyTotp({ code: code.replace(/\s/g, ''), trustDevice })
          : await authClient.twoFactor.verifyBackupCode({ code: code.trim(), trustDevice });
      if (err) return setError(authError(err.status));
      await done();
    } finally {
      setBusy(false);
    }
  };

  const passkey = async () => {
    setError(null);
    const res = await authClient.signIn.passkey();
    if (res?.error) return setError(t('auth.passkeyFailed'));
    await done();
  };

  if (step !== 'password') {
    return (
      <AuthFrame title={t('auth.twoFactorTitle')} lead={step === 'totp' ? t('auth.twoFactorLead') : undefined}>
        <form onSubmit={submit} className="space-y-2">
          <Field label={step === 'totp' ? t('auth.code') : t('auth.backupCode')}>
            {(id) => (
            <input
              id={id}
              className={`input w-full font-mono tracking-widest ${error ? 'input-error' : ''}`}
              inputMode={step === 'totp' ? 'numeric' : 'text'}
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              autoFocus
              required
            />
            )}
          </Field>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="checkbox checkbox-sm" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)} />
            {t('auth.trustDevice')}
          </label>
          {error && <p className="text-error text-sm">{error}</p>}
          <button type="submit" className="btn btn-primary mt-2 w-full" disabled={busy}>
            {busy && <span className="loading loading-spinner loading-sm" />}
            {t('auth.verify')}
          </button>
          <button
            type="button"
            className="btn btn-link btn-sm w-full"
            onClick={() => {
              setStep(step === 'totp' ? 'backup' : 'totp');
              setCode('');
              setError(null);
            }}>
            {step === 'totp' ? t('auth.useBackupCode') : t('auth.useTotp')}
          </button>
        </form>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={t('auth.signInTitle')} lead={t('auth.signInLead')}>
      {expired && <div className="alert alert-info alert-soft mb-4 text-sm">{t('auth.sessionExpired')}</div>}
      <form onSubmit={submit}>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">
            <label htmlFor="login-email">{t('auth.email')}</label>
          </legend>
          <label className="input w-full focus-within:outline-0">
            <span className="iconify lucide--mail text-base-content/80 size-5" />
            <input
              id="login-email"
              className="grow"
              type="email"
              autoComplete="username webauthn"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
          </label>
        </fieldset>
        <PasswordInput label={t('auth.password')} value={password} onChange={setPassword} autoComplete="current-password" />
        <div className="text-end">
          <Link className="text-base-content/80 text-xs hover:underline" to="/forgot-password">
            {t('auth.forgotPassword')}
          </Link>
        </div>
        {error && (
          <p className="text-error mt-3 text-sm" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-primary mt-5 w-full gap-2" disabled={busy}>
          {busy ? <span className="loading loading-spinner loading-sm" /> : <span className="iconify lucide--log-in size-4" />}
          {t('auth.signIn')}
        </button>
      </form>
      <div className="divider text-base-content/60 text-xs">{t('auth.or')}</div>
      <button type="button" className="btn btn-outline border-base-300 w-full gap-2" onClick={passkey}>
        <span className="iconify lucide--fingerprint size-4" />
        {t('auth.passkey')}
      </button>
    </AuthFrame>
  );
}
